import React, { useContext, useEffect, useState } from 'react';
import styles from './TelaCozinha.module.css';
import { PedidosContext } from '../../../shared/contexts/PedidosContext';
import CardUltimosPedidos from '../../../shared/components/CardUltimosPedidos';
import { ChefHat, LogOut, Receipt } from 'lucide-react';

// Tela pensada pra ficar aberta num tablet/TV fixo na cozinha, sem exigir
// login de uma pessoa: o dono/gerente digita uma vez o PIN configurado em
// Configurações e o dispositivo fica autenticado indefinidamente (o token
// dura 180 dias). Reaproveita o PedidosContext global — ele já lê
// 'token'/'estabelecimento_id' do localStorage e mantém SSE + polling.
function TelaCozinha() {
  const [autenticado, setAutenticado] = useState(
    () => localStorage.getItem('cozinha_token') && localStorage.getItem('tipo_usuario_sessao') === 'cozinha'
  );
  const [nomeEstabelecimento, setNomeEstabelecimento] = useState(
    () => localStorage.getItem('nomeEstabelecimento') || ''
  );
  const [pin, setPin] = useState('');
  const [erro, setErro] = useState('');
  const [enviando, setEnviando] = useState(false);
  const [relogio, setRelogio] = useState(new Date());

  const { pedidos, setPedidos, atualizarPedidos, loading } = useContext(PedidosContext);

  // Se já tem sessão de cozinha salva, garante que o PedidosContext está
  // apontando pro estabelecimento certo (útil logo após um refresh/boot do tablet).
  useEffect(() => {
    if (autenticado) {
      const idEstabelecimento = localStorage.getItem('estabelecimento_id');
      window.dispatchEvent(new CustomEvent('estabelecimentoChanged', { detail: { estabelecimentoId: idEstabelecimento } }));
      window.dispatchEvent(new Event('loginSuccess'));
    }
  }, [autenticado]);

  // Relógio no cabeçalho — ajuda a perceber se a tela travou
  useEffect(() => {
    const intervalo = setInterval(() => setRelogio(new Date()), 1000);
    return () => clearInterval(intervalo);
  }, []);

  const entrarComPin = async (e) => {
    e.preventDefault();
    if (!pin.trim()) return;

    setEnviando(true);
    setErro('');

    try {
      const API_URL = process.env.REACT_APP_API_URL;
      const response = await fetch(`${API_URL}/cozinha/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ pin: pin.trim() }),
      });

      const data = await response.json();

      if (!response.ok) {
        setErro(data.mensagemErro || 'PIN inválido.');
        return;
      }

      // Guarda a sessão da cozinha separadamente (pra distinguir de um login
      // normal) e também nas chaves compartilhadas que o resto do app usa,
      // pra reaproveitar o PedidosContext/SSE sem duplicar lógica de fetch.
      localStorage.setItem('cozinha_token', data.token);
      localStorage.setItem('tipo_usuario_sessao', 'cozinha');
      localStorage.setItem('token', data.token);
      localStorage.setItem('estabelecimento_id', String(data.id_estabelecimento));
      localStorage.setItem('nomeEstabelecimento', data.nomeEstabelecimento || '');

      setNomeEstabelecimento(data.nomeEstabelecimento || '');
      setAutenticado(true);
      setPin('');
    } catch (error) {
      setErro('Erro de conexão. Tente novamente.');
    } finally {
      setEnviando(false);
    }
  };

  // Mesmo endpoint/padrão usado no painel do operador (PainelControle.jsx) —
  // a cozinha só avança pendente -> em andamento -> pronto (ver
  // statusAvancoPermitido abaixo); "entregue" fica pra quem de fato entrega.
  const mudarStatusPedido = async (id_pedido, novoStatus) => {
    try {
      const API_URL = process.env.REACT_APP_API_URL;

      const response = await fetch(`${API_URL}/atualizar-item`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('token')}` },
        body: JSON.stringify({ numPedido: id_pedido, novoStatus }),
      });

      const data = await response.json();

      if (!response.ok || !data.success) {
        console.error('Erro ao atualizar status:', data.message);
        await atualizarPedidos();
        return;
      }

      setPedidos(pedidos.map((p) => (p.id_pedido === id_pedido ? { ...p, status: novoStatus } : p)));
    } catch (error) {
      console.error('Erro de comunicação ao atualizar status:', error);
      await atualizarPedidos();
    }
  };

  const sair = () => {
    if (!window.confirm('Sair da Tela da Cozinha? Vai ser preciso digitar o PIN de novo.')) return;
    localStorage.removeItem('cozinha_token');
    localStorage.removeItem('tipo_usuario_sessao');
    localStorage.removeItem('token');
    localStorage.removeItem('estabelecimento_id');
    setAutenticado(false);
  };

  if (!autenticado) {
    return (
      <div className={styles.loginContainer}>
        <form className={styles.loginCard} onSubmit={entrarComPin}>
          <ChefHat size={40} className={styles.loginIcon} />
          <h1>Tela da Cozinha</h1>
          <p>Digite o PIN de 6 dígitos configurado em Configurações.</p>

          {erro && <div className={styles.loginErro}>{erro}</div>}

          <input
            className={styles.loginInput}
            type="text"
            inputMode="numeric"
            maxLength={6}
            autoFocus
            placeholder="••••••"
            value={pin}
            onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
          />

          <button className={styles.loginBotao} type="submit" disabled={enviando || pin.length < 6}>
            {enviando ? 'Entrando...' : 'Entrar'}
          </button>
        </form>
      </div>
    );
  }

  // Sem filtro por dia: um pedido pendente/em preparo/pronto feito ontem (ou
  // há várias horas) continua precisando aparecer na cozinha até ser
  // finalizado — não faz sentido "sumir" só por ter cruzado a meia-noite.
  const colunas = [
    { status: 'pendente', titulo: 'Pendente' },
    { status: 'em andamento', titulo: 'Em preparo' },
    { status: 'pronto', titulo: 'Pronto' },
  ];

  return (
    <div className={styles.container}>
    {/*  <header className={styles.header}>
        <div className={styles.headerInfo}>
          <div>
            <h1>{nomeEstabelecimento || 'Tela da Cozinha'}</h1>
            <span className={styles.relogio}>
              {relogio.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
        </div>
        <button className={styles.btnSair} onClick={sair} title="Sair">
        Sair
        </button>
      </header>*/}

      {loading ? (
        <div className={styles.estadoVazio}>
          <div className={styles.spinner}></div>
          <p>Carregando pedidos...</p>
        </div>
      ) : (
        <div className={styles.colunas}>
          {colunas.map(({ status, titulo }) => {
            const pedidosDaColuna = pedidos
              .filter(p => p.status?.toLowerCase() === status)
              .sort((a, b) => new Date(a.data_pedido) - new Date(b.data_pedido));

            return (
              <section key={status} className={styles.coluna}>
                <div className={`${styles.colunaHeader} ${styles[`colunaHeader_${status.replace(/\s+/g, '')}`]}`}>
                  <h2>{titulo}</h2>
                  <span className={styles.contador}>{pedidosDaColuna.length}</span>
                </div>

                <div className={styles.colunaLista}>
                  {pedidosDaColuna.length === 0 ? (
                    <div className={styles.colunaVazia}>
                      <Receipt size={32} className={styles.icon}/>
                      <p>Nenhum pedido</p>
                    </div>
                  ) : (
                    pedidosDaColuna.map(pedido => (
                      <CardUltimosPedidos
                      key={pedido.id_pedido}
                      pedido={pedido}
                      exibirNomes={true}
                      mostrarBotoesStatus={true}
                      mudarStatusPedido={mudarStatusPedido}
                      statusAvancoPermitido={['em andamento', 'pronto']}
                      mostrarClienteEStatus={false}
                      exibirMeta={false}
                      typeView="cozinha"
                  />
                    ))
                  )}
                </div>
              </section>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default TelaCozinha;
